import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Entrada animada de un bloque.
 *
 * Deliberadamente simple: una animación CSS que corre al montar, con un retardo
 * opcional para escalonar una grilla. Sin IntersectionObserver y sin estado
 * oculto inicial.
 *
 * La versión anterior revelaba al scrollear, que se ve mejor, pero tenía un modo
 * de falla inaceptable: si el observer no disparaba, el contenido quedaba en
 * `opacity: 0` para siempre. En una app donde un paciente carga sus síntomas y
 * una enfermera mira una cola de alertas, una pantalla en blanco cuesta mucho
 * más de lo que aporta la animación. Esta versión no puede esconder nada: la
 * animación tiene `fill-mode: both` y termina siempre en visible, y con
 * `prefers-reduced-motion` se resuelve al instante.
 */
export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  /** Retardo en ms. Escalonar una grilla la hace ver deliberada, no lenta. */
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const mostrar = (): void => {
      element.style.opacity = '1';
      element.style.transform = 'none';
    };

    // Sin soporte del navegador, o con movimiento reducido, se muestra directo.
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      mostrar();
      return;
    }

    // Lo que ya está en pantalla al montar aparece sin esperar al observer.
    const box = element.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) {
      mostrar();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          mostrar();
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0, rootMargin: '0px 0px -6% 0px' },
    );

    observer.observe(element);

    /**
     * Red de seguridad.
     *
     * El intento anterior de esto dejaba el contenido invisible cuando el
     * observer no disparaba, y eso es mucho peor que no tener animación. Con el
     * temporizador, el peor caso es que un bloque aparezca sin transición.
     */
    const respaldo = window.setTimeout(mostrar, 1200);

    return () => {
      observer.disconnect();
      window.clearTimeout(respaldo);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      // El estado inicial va en línea, no en una clase: así ya está aplicado en
      // la primera pintura y el bloque no aparece para esconderse después.
      style={{
        opacity: 0,
        transform: 'translateY(16px)',
        transition: `opacity .5s cubic-bezier(.22,1,.36,1) ${delay}ms, transform .5s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Escalona los hijos de una grilla.
 *
 * El tope evita que la última tarjeta de una lista larga aparezca dos segundos
 * después: pasado cierto punto ya no se lee como ritmo sino como demora.
 */
export function RevealGroup({
  children,
  step = 70,
  maxDelay = 420,
  className,
}: {
  children: ReactNode[];
  step?: number;
  maxDelay?: number;
  className?: string;
}) {
  return (
    <div className={className}>
      {children.map((child, index) => (
        <Reveal key={index} delay={Math.min(index * step, maxDelay)}>
          {child}
        </Reveal>
      ))}
    </div>
  );
}
